import { useMutation, useQuery } from "@apollo/client";
import { useFormik } from "formik";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";

import { Button, getInput, Modal } from "../../../components/ui";
import { CREATE_JOB_REQUEST as createJobMutation } from "../../../graphql/mutation/Job";
import { FETCH_JOBS_LIST as fetchJobsList } from "../../../graphql/query/Job";
import { FETCH_JOB_TYPES } from "../../../graphql/query/JobType";
import { getJobTypeOptions, jobTypeStyles } from "../jobTypeOptions";
import { useEntities } from "../../../hooks/useEntities";
import useUser from "../../../hooks/useUser";
import { isWithin48Hours } from "../../../util";

const CreatedJobConfirmation = ({
  canViewJobs,
  closeModal,
  instanceCount,
  job,
  navigate,
}) => (
  <div className="flex w-full flex-col gap-4">
    <div>
      <div className="text-2xl font-bold">Job {job?.id} created</div>
      <p className="mt-2 text-sm text-gray-600">
        {instanceCount} {instanceCount === 1 ? "work was" : "works were"} added
        to the new job.
      </p>
    </div>
    <div className="flex flex-wrap gap-2 sm:gap-4">
      <Button action="default" label="Close" onClick={closeModal} />
      {canViewJobs && (
        <Button
          label="Go to Job"
          onClick={() => {
            closeModal?.();
            navigate(`/jobs/${job?.id}/details`);
          }}
        />
      )}
    </div>
  </div>
);

const getVisibleJobErrors = ({ errors = {}, submitCount = 0, touched = {} }) =>
  Object.fromEntries(
    Object.entries(errors).filter(
      ([fieldName]) => submitCount > 0 || touched[fieldName],
    ),
  );

const JOB_FORM_VALIDATION_MODE = {
  validateOnBlur: true,
  validateOnChange: false,
  validateOnMount: false,
};

const getTouchedErrorFields = errors =>
  Object.keys(errors).reduce(
    (touchedFields, fieldName) => ({
      ...touchedFields,
      [fieldName]: true,
    }),
    {},
  );

const WorksPreview = ({ works = [] }) => {
  if (works.length === 0) return null;

  return (
    <section aria-labelledby="selected-works-heading" className="min-w-0">
      <h3 id="selected-works-heading" className="mb-2 text-lg font-semibold">
        Works to add ({works.length})
      </h3>
      <div className="max-h-72 overflow-y-auto rounded border border-gray-200">
        <div className="hidden grid-cols-[4rem_minmax(7rem,0.8fr)_minmax(10rem,1.4fr)_minmax(8rem,1fr)] gap-3 border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 md:grid">
          <span>Image</span>
          <span>Reference</span>
          <span>Title</span>
          <span>Artist</span>
        </div>
        {works.map(work => (
          <div
            className="grid grid-cols-[3.5rem_minmax(0,1fr)] gap-3 border-b border-gray-200 p-3 last:border-b-0 md:grid-cols-[4rem_minmax(7rem,0.8fr)_minmax(10rem,1.4fr)_minmax(8rem,1fr)] md:items-center"
            key={work.id}
          >
            <div className="flex h-14 w-14 items-center justify-center overflow-hidden rounded bg-gray-100">
              {work.image ? (
                <img
                  alt=""
                  className="h-full w-full object-contain"
                  src={work.image}
                />
              ) : (
                <span className="text-center text-[10px] text-gray-400">
                  No image
                </span>
              )}
            </div>
            <div className="min-w-0 md:contents">
              <div className="truncate font-medium" title={work.reference}>
                {work.reference}
              </div>
              <div className="truncate" title={work.title}>
                {work.title}
              </div>
              <div className="truncate text-gray-600" title={work.artist}>
                {work.artist}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

const NewJob = props => {
  const {
    closeModal,
    exhibitionId = null,
    instanceIds = [],
    selectedWorks = [],
    showCreatedJobResult = false,
    title = "Add a New Job",
  } = props || {};
  const navigate = useNavigate();
  const { hasPermission } = useUser();
  const canViewJobs = hasPermission("JOB_MODULE");
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [createdJob, setCreatedJob] = useState(null);
  const { data: { jobTypes = [] } = {} } = useQuery(FETCH_JOB_TYPES);
  const [createJob, { loading }] = useMutation(createJobMutation(), {
    refetchQueries: [fetchJobsList],
  });
  const { entities: pickupGroups } = useEntities({
    variables: { input: { hide: 0, user: 0, order: ["name"] } },
  });

  const formik = useFormik({
    initialValues: {
      date: new Date(),
      description: "",
      dueDate: "",
      duetime: "",
      pickupId: "",
      statusId: 1,
      typeId: "",
    },
    ...JOB_FORM_VALIDATION_MODE,
    validationSchema: Yup.object({
      typeId: Yup.string().trim().required("You must select type for new job."),
      description: Yup.string()
        .trim()
        .required("You must enter description for new job."),
      dueDate: Yup.date()
        .transform((value, originalValue) =>
          originalValue === "" ? null : value,
        )
        .typeError("You must select a valid due date for new job.")
        .required("You must select due date for new job."),
      duetime: Yup.string()
        .required("Due-Time must be greater than or equal to 10 AM GMT")
        .test(
          "validation",
          `Due-Time must be greater than or equal to 10 AM GMT`,
          function (value) {
            if (!value) return true;
            const splitTime = value.split(":");
            return Number(splitTime[0]) >= 10;
          },
        ),
      pickupId: Yup.number()
        .typeError("You must enter pickup for new job.")
        .required("You must enter pickup for new job."),
    }),
    onSubmit: async (values, { setErrors, resetForm }) => {
      const error = {};
      const updatedDueTime = new Date(`${values.dueDate} ${values.duetime}`);

      if (values?.duetime && !(values.duetime.split(":")[0] >= 10)) {
        error.duetime = `Due-Time must be greater than or equal to 10 AM GMT`;
      }

      if (Object.keys(error).length <= 0) {
        createJob({
          variables: {
            input: {
              ...values,
              duetime: updatedDueTime,
              ...(exhibitionId ? { exhibitionId: +exhibitionId } : {}),
              ...(instanceIds.length > 0 ? { instanceIds } : {}),
            },
          },
        }).then(resp => {
          const {
            data: { createJob: { success, job } = {} },
          } = resp || {};
          if (success) {
            resetForm();
            if (showCreatedJobResult) {
              setCreatedJob(job);
            } else {
              closeModal();
              if (canViewJobs) {
                navigate(`/jobs/${job?.id}/details`);
              }
            }
          }
        });
      } else {
        setErrors(error);
      }
    },
  });

  const validationTimers = useRef({});
  const clearFieldValidation = name => {
    clearTimeout(validationTimers.current[name]);
    delete validationTimers.current[name];
  };
  const clearPendingValidation = () => {
    Object.keys(validationTimers.current).map(clearFieldValidation);
  };

  useEffect(() => {
    const timers = validationTimers.current;
    return () => Object.values(timers).map(timer => clearTimeout(timer));
  }, []);

  const validateField = async name => {
    clearFieldValidation(name);
    await formik.setFieldTouched(name, true, false);
    await formik.validateField(name);
  };
  const changeField = (name, value) => {
    clearFieldValidation(name);
    formik.setFieldValue(name, value ?? "", false);
    validationTimers.current[name] = setTimeout(() => validateField(name), 500);
  };

  const inputs = [
    {
      label: "Type",
      name: "typeId",
      inputId: "typeId",
      type: "multi-select",
      isClearable: true,
      onChange: async value => {
        await formik.setFieldValue("typeId", value ?? "", false);
        await validateField("typeId");
      },
      placeholder: "Select a type",
      options: getJobTypeOptions(jobTypes),
      styles: jobTypeStyles,
    },
    { label: "Description", name: "description", type: "text" },
    { label: "Due Date", name: "dueDate", type: "date" },
    {
      label: "Due Time",
      name: "duetime",
      type: "time",
      disabled: !formik.getFieldProps("dueDate").value,
    },
    {
      label: "Pickup Group",
      name: "pickupId",
      inputId: "pickupId",
      type: "multi-select",
      onChange: value => changeField("pickupId", value),
      options:
        pickupGroups?.edges?.map(item => ({
          label: item?.name,
          value: item?.id,
        })) || [],
    },
  ];

  const inputFormik = {
    ...formik,
    errors: getVisibleJobErrors(formik),
    getFieldProps: name => ({
      ...formik.getFieldProps(name),
      onChange: event => changeField(name, event.target.value),
      onBlur: () => validateField(name),
    }),
  };

  const handleCreateClick = async () => {
    clearPendingValidation();
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      await formik.setTouched(getTouchedErrorFields(errors), false);
      return;
    }

    const dueWithin48Hours = isWithin48Hours({
      dueDate: formik.values.dueDate,
      duetime: formik.values.duetime,
    });
    if (dueWithin48Hours) {
      setIsConfirmOpen(true);
    } else {
      formik.submitForm();
    }
  };

  const confirmModalProps = {
    isOpen: isConfirmOpen,
    scale: "sm",
    closeOnBackdrop: true,
    title: "Check with Warehouse",
    description:
      "This job is being requested within 48 hours. Please contact the Warehouse team directly to confirm scheduling before proceeding.",
    onClose: () => setIsConfirmOpen(false),
    renderBody: ({ closeModal: closeConfirmModal }) => (
      <div className="mt-4 flex">
        <Button
          className="mr-3"
          label="Cancel"
          action="default"
          onClick={() => closeConfirmModal?.()}
        />
        <Button
          label="Confirm"
          loading={loading}
          disabled={loading}
          onClick={() => {
            closeConfirmModal?.();
            formik.submitForm();
          }}
        />
      </div>
    ),
  };

  if (createdJob) {
    return (
      <CreatedJobConfirmation
        canViewJobs={canViewJobs}
        closeModal={closeModal}
        instanceCount={instanceIds.length}
        job={createdJob}
        navigate={navigate}
      />
    );
  }

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="flex-1 text-2xl font-bold">{title}</div>
        <div className="flex gap-2 sm:gap-4">
          <Button
            action="default"
            label="Cancel"
            onClick={() => {
              closeModal();
              formik.resetForm();
            }}
          />
          <Button
            label={"Create"}
            loading={loading}
            disabled={loading || !formik.dirty}
            onClick={handleCreateClick}
          />
          <Modal {...confirmModalProps} />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {inputs?.map((item, index) => {
          const inputProps = {
            ...item,
            formik: inputFormik,
          };
          return <div key={index}>{getInput(inputProps)}</div>;
        })}
      </div>
      <WorksPreview works={selectedWorks} />
    </div>
  );
};

export {
  CreatedJobConfirmation,
  getTouchedErrorFields,
  getVisibleJobErrors,
  JOB_FORM_VALIDATION_MODE,
  WorksPreview,
};
export default NewJob;
